'use client';

import { useState } from 'react';

import { colorName, colorTint, type Media, type Product } from '@/entities/product';
import { cx } from '@/shared/lib/cx';
import { Badge, MediaTag } from '@/shared/ui/badge';
import { Dialog } from '@/shared/ui/dialog';
import { Icon } from '@/shared/ui/icon';
import { MediaFrame } from '@/shared/ui/media-frame';

import styles from './product-gallery.module.scss';

const SLOTS = 6;

type Slide = {
  id: string;
  media: Media | null;
  video: boolean;
  label: string;
  sub: string;
  tint: string | null;
};

function slides(product: Product, colorId: string | null, size: string | null): Slide[] {
  const color = colorName(product, colorId);
  const tint = colorTint(product, colorId);
  const photos = product.media.filter((item) => item.kind === 'photo' || item.kind === 'sketch');
  // Real photos first; the rest of the grid keeps the design's placeholders until the shoot arrives.
  return Array.from({ length: SLOTS }, (_, index): Slide => {
    const video = index === SLOTS - 1;
    if (video)
      return {
        id: 'video',
        media: null,
        video,
        label: 'Видео',
        sub: color ? `${color} · ролик о товаре` : 'ролик о товаре',
        tint: null,
      };
    const media = photos[index] ?? null;
    return {
      id: `photo-${index + 1}`,
      media,
      video,
      label: size ? `Фото размера ${size} · ${index + 1}` : `Фото ${index + 1}`,
      sub: color ? `${color} · ${size ? `модель в размере ${size}` : 'товар'}` : 'товар',
      tint,
    };
  });
}

export type ProductGalleryProps = {
  product: Product;
  colorId: string | null;
  size: string | null;
  preorder: boolean;
  showDemoNotes: boolean;
};

export function ProductGallery({
  product,
  colorId,
  size,
  preorder,
  showDemoNotes,
}: ProductGalleryProps) {
  const items = slides(product, colorId, size);
  const [start, setStart] = useState(0);
  const [zoom, setZoom] = useState<number | null>(null);
  const maxStart = items.length - 2;
  const current = zoom === null ? null : (items[zoom] ?? null);

  const frame = (slide: Slide, index: number, large = false) => (
    <MediaFrame
      src={slide.media?.src ?? null}
      alt={slide.media?.alt ?? `${product.name}: ${slide.label}`}
      kind={slide.media?.kind === 'sketch' ? 'sketch' : 'photo'}
      position={slide.media?.position ?? null}
      sizes={large ? '(min-width: 1024px) 60rem, 100vw' : '(min-width: 1024px) 26rem, 100vw'}
      priority={index === 0 && !large}
      tint={slide.video ? null : slide.tint}
      className={cx(large ? styles['zoom-frame'] : styles.frame, slide.video && styles.video)}
      placeholder={{
        label: slide.label,
        sub: large ? `${slide.sub} · увеличенный вид · плейсхолдер` : slide.sub,
        icon: slide.video ? (
          <Icon name="play" size={large ? 64 : 44} strokeWidth={1.2} className={styles.play} />
        ) : undefined,
      }}
    >
      {/* 03B: the badge marks the first visible photo, not the first slide. */}
      {!large && index === start && preorder && <Badge tone="accent">Предзаказ</Badge>}
      {!large && showDemoNotes && slide.media?.demoTag && (
        <MediaTag>{slide.media.demoTag}</MediaTag>
      )}
    </MediaFrame>
  );

  return (
    <div className={styles.root}>
      <div className={styles.stage}>
        <ul className={styles.track} style={{ '--start': start }} aria-label="Фото товара">
          {items.map((slide, index) => (
            <li key={slide.id} className={styles.slide}>
              <button
                type="button"
                className={styles.tile}
                onClick={() => setZoom(index)}
                aria-label={`Открыть увеличенное фото: ${slide.label}`}
              >
                {frame(slide, index)}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className={styles.arrow}
          data-side="prev"
          aria-label="Предыдущие фото"
          aria-disabled={start === 0 || undefined}
          onClick={() => setStart((value) => Math.max(0, value - 1))}
        >
          <Icon name="chevron-left" size={20} strokeWidth={1.4} />
        </button>
        <button
          type="button"
          className={styles.arrow}
          data-side="next"
          aria-label="Следующие фото"
          aria-disabled={start >= maxStart || undefined}
          onClick={() => setStart((value) => Math.min(maxStart, value + 1))}
        >
          <Icon name="chevron-right" size={20} strokeWidth={1.4} />
        </button>
      </div>
      <div className={styles.thumbs}>
        {items.map((slide, index) => {
          const on = index === start || index === start + 1;
          return (
            <button
              key={slide.id}
              type="button"
              className={styles.thumb}
              aria-pressed={on}
              onClick={() => setStart(Math.min(index, maxStart))}
              aria-label={`Показать ${slide.label}`}
              data-video={slide.video || undefined}
              style={slide.tint && !slide.video ? { '--thumb-bg': slide.tint } : undefined}
            >
              {slide.label}
            </button>
          );
        })}
      </div>
      <Dialog
        theme="editorial"
        open={zoom !== null}
        onOpenChange={(next) => {
          if (!next) setZoom(null);
        }}
        title="Увеличенное фото"
        width="60rem"
        backdrop="zoom"
        shadow="none"
        className={styles.zoom}
      >
        {current && zoom !== null && (
          <div className={styles['zoom-body']}>
            <div className={styles['zoom-head']}>
              <span className={styles.counter}>
                {zoom + 1} / {items.length} · {current.label} · {current.sub}
              </span>
              <button
                type="button"
                className={styles.close}
                onClick={() => setZoom(null)}
                aria-label="Закрыть увеличенное фото"
              >
                ×
              </button>
            </div>
            <div className={styles['zoom-stage']}>
              {frame(current, zoom, true)}
              <button
                type="button"
                className={styles.arrow}
                data-side="prev"
                data-zoom
                aria-label="Предыдущее фото"
                aria-disabled={zoom === 0 || undefined}
                onClick={() => setZoom((value) => Math.max(0, (value ?? 0) - 1))}
              >
                <Icon name="chevron-left" size={20} strokeWidth={1.4} />
              </button>
              <button
                type="button"
                className={styles.arrow}
                data-side="next"
                data-zoom
                aria-label="Следующее фото"
                aria-disabled={zoom === items.length - 1 || undefined}
                onClick={() => setZoom((value) => Math.min(items.length - 1, (value ?? 0) + 1))}
              >
                <Icon name="chevron-right" size={20} strokeWidth={1.4} />
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
